import { Grid, GridRow } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';
import { EpochBasicsForm } from '../../Lessons/Forms/Basics/EpochBasicsForm';
import { EpochLessonUnitForm } from '../../Lessons/Forms/LessonUnits/EpochLessonUnitForm';
import { epochSchema } from '../../Lessons/Forms/validation/schema';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useCurriculum } from '../hooks/useCurriculum';
import { CurriculumFormType } from '../types';
import { CurriculumClassesForm } from './Classes/CurriculumClassesForm';

type CurriculumBlockContainerFormProps = {
  versionUuid?: string | null;
  curriculumUuid?: string | null;
  defaultInitialValues: Partial<CurriculumFormType>;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const CurriculumBlockContainerForm = observer(
  ({ curriculumUuid, versionUuid, defaultInitialValues, onLoad, onClose }: CurriculumBlockContainerFormProps) => {
    const { t } = useTranslation();

    const { createCurriculum, updateCurriculum, queryData, tableData } = useCurriculum({
      versionUuid: versionUuid ?? '',
      grade: undefined,
    });

    const curriculum = queryData?.curricula.find((curriculum) => {
      return curriculum.uuid === curriculumUuid;
    });

    const status = tableData.find((curriculum) => {
      return curriculum.uuid === curriculumUuid;
    })?.editStatus;

    const initialValues: CurriculumFormType = curriculum
      ? {
          uuid: curriculum.uuid,
          comment: curriculum.comment,
          lessonUnit: curriculum.curriculumUnit.map((cu) => ({
            uuid: cu.uuid,
            duration: cu.duration,
            count: cu.count,
            subjectContainerUuid: cu.subjectContainer?.uuid,
            factor: null,
            blocked: false,
            lessonUnitType: cu.subjectContainer?.uuid ? 'epoch' : 'subjectHour',
          })),
          curriculumGrades: curriculum.curriculumGrades,
          subject: curriculum.subject,
          subTitle: curriculum.subTitle,
        }
      : { ...defaultInitialValues };

    const handleSubmit = async (values: CurriculumFormType, formikHelpers: FormikHelpers<CurriculumFormType>) => {
      onLoad(true);

      if (curriculum) {
        const { error } = await updateCurriculum({ curriculumUuid: curriculum.uuid, values });
        if (error) {
          showUserDbErrorToast();
        } else {
          showSuccessUpdateToast();
        }
      } else {
        const { error } = await createCurriculum(values);
        if (error) {
          showUserDbErrorToast();
        } else {
          showSuccessCreateToast();
        }
      }

      onLoad(false);
      formikHelpers.resetForm();
      onClose();
    };

    return (
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={epochSchema}>
        {({ values, isSubmitting, errors, resetForm }) => {
          return (
            <Form>
              <Grid useFormGap>
                <GridRow>
                  <EpochBasicsForm status={status} preFilterSubjectContainers={true} />
                </GridRow>

                <GridRow headline={t('classes.title', { count: 2 })}>
                  {/* TODO: filter all grades with Lessons for this container too */}
                  <CurriculumClassesForm enableGroups={false} selectableGrades={values.subject?.grades ?? []} />
                </GridRow>

                <GridRow headline={t('subject.subjectHour')}>
                  <EpochLessonUnitForm disabled={false} key={values.subject?.uuid} />
                </GridRow>
              </Grid>

              <ModalBottomButtons
                closeButton={{
                  callback: () => {
                    resetForm();
                    onClose();
                  },
                }}
                isLoading={isSubmitting}
                errors={errors}
              />
            </Form>
          );
        }}
      </Formik>
    );
  },
);
